import { createContext, useCallback, useContext, useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './api';

export type Role = 'agency_principal' | 'agency_rep' | 'internal_seller' | 'reviewer' | 'admin';

export type Me = {
  id: number;
  email: string;
  role: Role;
  firstName: string;
  lastName: string;
  name: string;
  contactInfoId: number;
  phone: string | null;
  title: string | null;
  agencyId: number | null;
  agencyName: string | null;
  /** Whether the assistant panel shows: always for staff, and for a rep when Circdal turned it on for their agency. */
  assistant?: boolean;
};

type AuthState = {
  user: Me | null;
  loading: boolean;
  isStaff: boolean;
  isAdmin: boolean;
  isInternal: boolean;
  /** The assistant panel shows for this person (staff, or a rep whose agency has it on). */
  hasAssistant: boolean;
  /** Overlap and duplicate warnings are for the Circdal team only, never agency reps or principals. */
  seesOverlaps: boolean;
  refresh: () => Promise<unknown>;
  logout: () => Promise<void>;
  setUser: (u: Me | null) => void;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const me = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return (await api.get<{ user: Me }>('/auth/me')).user;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

  const setUser = useCallback((u: Me | null) => qc.setQueryData(['me'], u), [qc]);

  useEffect(() => {
    const onUnauthorized = () => {
      qc.setQueryData(['me'], null);
    };
    window.addEventListener('circdal:unauthorized', onUnauthorized);
    return () => window.removeEventListener('circdal:unauthorized', onUnauthorized);
  }, [qc]);

  const logout = useCallback(async () => {
    await api.post('/auth/logout').catch(() => {});
    qc.clear();
    qc.setQueryData(['me'], null);
  }, [qc]);

  const user = me.data ?? null;
  const isInternal = !!user && ['internal_seller', 'reviewer', 'admin'].includes(user.role);
  const value: AuthState = {
    user,
    loading: me.isLoading,
    isStaff: !!user && (user.role === 'reviewer' || user.role === 'admin'),
    isAdmin: user?.role === 'admin',
    isInternal,
    hasAssistant: !!user && (user.role === 'reviewer' || user.role === 'admin' || user.assistant === true),
    // The same rule as the API's seesOverlaps: Circdal users see overlaps, agency users do not.
    seesOverlaps: isInternal,
    refresh: () => me.refetch(),
    logout,
    setUser,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
